import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import type {
  PremiseConfig,
  SchemeBinding,
  SchemeContent,
} from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import { isCarePremise } from "@shared/types/foyer";
import { hasPromotionsLicense, hasStreamingTvLicense } from "@shared/products";
import { certifiedEnabledIds } from "@shared/app-merge";
import {
  certificationOf,
  CERTIFICATION_LABEL,
} from "@shared/app-certification";
import type { TemplateSubSection } from "../PremiseSidebar";
import SchemePicker from "./SchemePicker";
import DesignTokenEditor from "./DesignTokenEditor";
import TypographyEditor from "./TypographyEditor";
import ScreensEditor from "./ScreensEditor";
import ScreenStateBar from "./ScreenStateBar";
import {
  BrandingEditor,
  BackgroundEditor,
  AppsEditor,
  StationsEditor,
  InfoEditor,
  WifiEditor,
  WelcomeEditor,
  RelativesEditor,
  InstagramEditor,
  WeatherEditor,
  HomeCardsEditor,
  SlotsEditor,
} from "./ContentEditor";
import CustomScreenEditor from "./CustomScreenEditor";
import MenuEditor from "./MenuEditor";
import VilmerEditor from "./VilmerEditor";
import { foyerApi, type AppRecord } from "../../http/foyer";
import type { PremiseCommit } from "../../context/PremiseWorkspace";
import { EditSectionCard, useCommitNext } from "./EditSection";
import { FORM_SUBS, subNoun } from "./templateSubMessages";

/** Stands in for `onCommit` when the editor is in buffered mode (never called). */
const NO_COMMIT: PremiseCommit = () => Promise.resolve(false);

/**
 * The global app catalogue and what it pins, for one premise.
 *
 * Fetched ONCE per page and shared by the Apps pane and the screen list. They
 * ask the same question of it - which certified apps are switched on (NFLX-4) -
 * and a second fetch is a second answer that can be a beat behind the first.
 */
export function useTemplateApps(premise: PremiseConfig) {
  const { t } = useTranslation("templates");
  const [apps, setApps] = useState<AppRecord[] | null>(null);
  useEffect(() => {
    let alive = true;
    foyerApi
      .listApps()
      .then((list) => alive && setApps(Array.isArray(list) ? list : []))
      .catch(() => alive && setApps([]));
    return () => {
      alive = false;
    };
  }, []);

  // A certified app that is ON pins part of the arrangement: Netflix must be
  // reachable, so the Apps screen stops being switchable while it is showing.
  // NFLX-6: a care premise offers no certified app at all. Read from the DRAFT,
  // not the saved config, so a change takes effect before Save.
  const isCare = isCarePremise({
    type: premise.type,
    segment: premise.segment,
  });
  const certifiedOn = certifiedEnabledIds(
    apps,
    premise.scheme?.content.disabledAppIds,
    isCare
  );
  const certNote = certifiedOn.size
    ? t("templateSubEditor.requiredWhileOn", {
        names: [
          ...new Set(
            (apps ?? [])
              .filter((a) => certifiedOn.has(a.id))
              .map((a) => CERTIFICATION_LABEL[certificationOf(a)!])
          ),
        ].join(" / "),
      })
    : undefined;
  return { apps, isCare, certifiedOn, certNote };
}

export type TemplateApps = ReturnType<typeof useTemplateApps>;

interface Props {
  premise: PremiseConfig;
  sub: TemplateSubSection;
  /**
   * BUFFERED mode: every edit is handed back and nothing is saved here. Used
   * where a form around the editor owns the Save (a menu item's drawer, or the
   * form drawer this component opens for itself).
   */
  onChange?: (next: PremiseConfig) => void;
  /**
   * SAVING mode: edits are written to the premise and toasted. Discrete
   * actions (a switch, a reorder, a section's own Save) write at once; form
   * sub-sections (colours, branding, Wi-Fi...) show a card whose Edit button
   * opens a drawer with one Save. Wins over `onChange` when both are given.
   */
  onCommit?: PremiseCommit;
  /** From {@link useTemplateApps}, owned by the page. */
  apps: TemplateApps;
  /** Move to another sub-section - lets the Screens board link into each editor. */
  onSelectSub?: (sub: TemplateSubSection) => void;
  /**
   * Show the screen's own on/off bar above its editor. The Start & navigation
   * pages state on/off in their own "Active" control, so they turn this off.
   */
  stateBar?: boolean;
}

/**
 * The controls for ONE template sub-section, without any page chrome.
 *
 * Shared by the old single-column `TemplateEditor` (still used for Promotions'
 * home-screen slot) and the Start & navigation pages, which place the same
 * editors in a list / editor / preview layout. One switch, so an editor added
 * here reaches both.
 */
export default function TemplateSubEditor({
  premise,
  sub,
  onChange: onBuffered,
  onCommit,
  apps: { apps, isCare, certifiedOn, certNote },
  onSelectSub,
  stateBar = true,
}: Props) {
  const binding = premise.scheme;
  const { t } = useTranslation(["templates", "common"]);
  const commitNext = useCommitNext(premise, onCommit ?? NO_COMMIT);
  const noun = subNoun(t, sub);
  const where = premise.premiseName;
  /** One edit, in whichever mode this editor is in. */
  const onChange = (next: PremiseConfig) => {
    if (!onCommit) {
      onBuffered?.(next);
      return;
    }
    const picked =
      sub === "picker" && next.scheme
        ? (SCHEME_MANIFESTS[next.scheme.key]?.name ?? next.scheme.key)
        : null;
    void commitNext(next, {
      success: picked
        ? t("templateSubEditor.templateSelected", { name: picked, where })
        : t("templateSubEditor.sectionUpdated", { section: noun, where }),
      failure: picked ? "selectTemplateFor" : "saveSectionFor",
      failureParams: picked
        ? { name: where }
        : { section: noun.toLowerCase(), name: where },
    });
  };
  const setBinding = (next: SchemeBinding) =>
    onChange({ ...premise, scheme: next });
  const setContent = (content: SchemeContent) => {
    if (binding) setBinding({ ...binding, content });
  };
  const manifest = binding ? SCHEME_MANIFESTS[binding.key] : undefined;
  const loc = {
    supportedLocales: premise.supportedLocales,
    defaultLocale: premise.defaultLocale,
  };

  if (sub === "picker") {
    return <SchemePicker premise={premise} onChange={onChange} />;
  }

  if (!binding) {
    return (
      <Typography variant="body2" color="text.secondary">
        <Trans
          t={t}
          i18nKey="templateSubEditor.pickTemplateFirst"
          components={{ strong: <strong /> }}
        />
      </Typography>
    );
  }

  // In saving mode a FORM sub-section is a card + drawer, not live fields.
  const formCard = !!onCommit && FORM_SUBS.has(sub);

  return (
    <>
      {!formCard && manifest && sub === "design" && (
        <DesignTokenEditor
          manifest={manifest}
          value={binding.tokens}
          onChange={(tokens) => setBinding({ ...binding, tokens })}
        />
      )}
      {!formCard && manifest && sub === "typography" && (
        <TypographyEditor
          manifest={manifest}
          value={binding.tokens}
          onChange={(tokens) => setBinding({ ...binding, tokens })}
        />
      )}
      {manifest && sub === "knobs" && (
        <ScreensEditor
          manifest={manifest}
          value={binding.knobs}
          content={binding.content}
          onChange={(knobs) => setBinding({ ...binding, knobs })}
          onOpenSub={onSelectSub}
          hasPromotions={hasPromotionsLicense(premise.products)}
          appsCertified={certifiedOn.size > 0}
          certificationNote={certNote}
          hasStreamingTv={hasStreamingTvLicense(premise.products)}
        />
      )}

      {/* Every screen editor states its own on/off, so you never have to leave
          the page you are filling in to discover it isn't being shown. That
          includes Promotions → Home screen slot: `showPromotions` is a banner,
          not a menu item, so Start view no longer lists it and this bar is its
          one switch. */}
      {stateBar && sub !== "knobs" && (
        <ScreenStateBar
          sub={sub}
          manifest={manifest}
          value={binding.knobs}
          onChange={(knobs) => setBinding({ ...binding, knobs })}
        />
      )}
      {formCard && (
        <>
          <EditSectionCard
            premise={premise}
            onCommit={onCommit as PremiseCommit}
            title={noun}
            description={t("templateSubEditor.openEditor", {
              section: noun.toLowerCase(),
            })}
            success={t("templateSubEditor.sectionUpdated", {
              section: noun,
              where,
            })}
            failure="saveSectionFor"
            failureParams={{ section: noun.toLowerCase(), name: where }}
            data-testid={`edit-section-${sub}`}
          >
            {(local, setLocal) => (
              <TemplateSubEditor
                premise={local}
                sub={sub}
                onChange={setLocal}
                apps={{ apps, isCare, certifiedOn, certNote }}
                stateBar={false}
              />
            )}
          </EditSectionCard>
        </>
      )}
      {!formCard && sub === "branding" && (
        <BrandingEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {!formCard && manifest && sub === "background" && (
        <BackgroundEditor
          content={binding.content}
          manifest={manifest}
          {...loc}
          onChange={setContent}
        />
      )}
      {sub === "apps" && (
        <AppsEditor
          content={binding.content}
          {...loc}
          global={apps}
          isCare={isCare}
          onChange={setContent}
        />
      )}
      {sub === "radio" && (
        <StationsEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {sub === "info" && (
        <InfoEditor content={binding.content} {...loc} onChange={setContent} />
      )}
      {!formCard && sub === "menu" && (
        <MenuEditor content={binding.content} {...loc} onChange={setContent} />
      )}
      {!formCard && sub === "vilmer" && (
        <VilmerEditor
          content={binding.content}
          rooms={premise.rooms ?? []}
          onChange={setContent}
        />
      )}
      {manifest && sub === "slots" && (
        <SlotsEditor
          content={binding.content}
          manifest={manifest}
          {...loc}
          onChange={setContent}
        />
      )}
      {!formCard && sub === "wifi" && (
        <WifiEditor content={binding.content} {...loc} onChange={setContent} />
      )}
      {!formCard && sub === "welcome" && (
        <WelcomeEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {!formCard && sub === "relatives" && (
        <RelativesEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {!formCard && sub === "instagram" && (
        <InstagramEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {sub === "weather" && (
        <WeatherEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {!formCard && manifest && sub === "custom" && (
        <CustomScreenEditor
          manifest={manifest}
          knobs={binding.knobs}
          onKnobs={(knobs) => setBinding({ ...binding, knobs })}
          content={binding.content}
          onChange={setContent}
          {...loc}
        />
      )}
      {!formCard && sub === "icons" && (
        <HomeCardsEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
    </>
  );
}
